import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { RecommendationsPanel } from '@/components/recommendations/RecommendationsPanel'
import { Recommendation } from '@/types'
import { Download, RefreshCw, Target, TrendingUp } from 'lucide-react'
import { cn } from '@/utils/cn'
import { apiClient } from '@/services/apiClient'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

export function RecommendationsPage() {
  const { user } = useAuth()
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchRecommendations()
  }, [user?.businessId])

  const fetchRecommendations = async () => {
    const businessId = user?.businessId
    if (!businessId) {
      setIsLoading(false)
      setError('No business linked to your account. Please create a business profile first.')
      return
    }

    try {
      setError(null)
      const response = await apiClient.get<any>(`/recommendations/${businessId}`)
      const data = response.data

      // Map API response to Recommendation type
      const mapped: Recommendation[] = (data.recommendations || []).map((rec: any) => ({
        id: rec.id,
        businessId: rec.businessId || businessId,
        title: rec.title,
        description: rec.description,
        category: rec.category || 'data_quality',
        priority: rec.priority || 'medium',
        impact: rec.impact || 5,
        effort: rec.effort || 'medium',
        timeframe: rec.timeframe || '1-2 weeks',
        actionSteps: (rec.actionSteps || []).map((step: any) => ({
          id: step.id,
          title: step.title,
          description: step.description,
          completed: step.completed || false,
        })),
        status: rec.status || 'pending',
        createdAt: rec.createdAt || new Date().toISOString(),
        updatedAt: rec.updatedAt || new Date().toISOString(),
      }))

      setRecommendations(mapped)
    } catch (err: any) {
      console.error('Failed to fetch recommendations:', err)
      setError('Unable to load recommendations. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleComplete = (recommendationId: string) => {
    setRecommendations(prev =>
      prev.map(rec =>
        rec.id === recommendationId
          ? { ...rec, status: 'completed' as const, updatedAt: new Date().toISOString() }
          : rec
      )
    )
  }

  const handleDismiss = (recommendationId: string) => {
    setRecommendations(prev =>
      prev.map(rec =>
        rec.id === recommendationId
          ? { ...rec, status: 'dismissed' as const, updatedAt: new Date().toISOString() }
          : rec
      )
    )
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await fetchRecommendations()
    setIsRefreshing(false)
  }

  const handleExport = () => {
    // Generate CSV export
    const csvRows = [
      ['Title', 'Category', 'Priority', 'Impact', 'Status', 'Timeframe'].join(','),
      ...recommendations.map(r =>
        [r.title, r.category, r.priority, r.impact, r.status, r.timeframe]
          .map(v => `"${v}"`)
          .join(',')
      )
    ]
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'recommendations.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Calculate statistics
  const stats = {
    total: recommendations.length,
    pending: recommendations.filter(r => r.status === 'pending').length,
    inProgress: recommendations.filter(r => r.status === 'in_progress').length,
    completed: recommendations.filter(r => r.status === 'completed').length,
    totalImpact: recommendations
      .filter(r => r.status !== 'completed' && r.status !== 'dismissed')
      .reduce((sum, r) => sum + r.impact, 0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading recommendations...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Recommendations
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Actionable insights to improve your TLBH score
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
            Refresh
          </button>

          <button
            onClick={handleExport}
            disabled={recommendations.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">{error}</p>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
            <Target className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center">
              <span className="text-yellow-600 text-xl">⏳</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
              <p className="text-3xl font-bold text-blue-600">{stats.inProgress}</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-xl">🚀</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Potential Impact</p>
              <p className="text-3xl font-bold text-green-600">+{stats.totalImpact}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Score points if completed
          </p>
        </div>
      </div>

      {/* Recommendations Panel */}
      {recommendations.length > 0 ? (
        <RecommendationsPanel
          recommendations={recommendations}
          showCompleted={false}
          onComplete={handleComplete}
          onDismiss={handleDismiss}
        />
      ) : !error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Recommendations Yet</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Generate an AI score for your business to receive personalized recommendations.
          </p>
        </div>
      )}
    </div>
  )
}
